import type { OverlayBundle } from "../api/resources";

/**
 * The free tier shows each cue on its own, the way the Pro video does (owner, 2026-09-25: "whenever I
 * click on them, I just get all of the feedback in one go"). A CUE is one overlay on one position — the
 * renderer's segmentKey, the overlay class — and everything it draws (its line, its readout cards, its
 * caption) belongs to it. The video shows one surface per cue; the still writer leaves one still per cue
 * ({sfcId}-{P}-{cue}-still.png); so the coaching list is one row per cue, too.
 *
 * It used to be one row per TEXT SHAPE. Every golf overlay draws a headline plus readout cards
 * (OverlayReadout.RightRows, tagged card: "true"), so four cues came out as eight rows and a readout
 * like "Shaft 52°" stood alone — twice, when two cues measured the shaft.
 */
export interface CueRow {
  /** `${pLabel}:${overlay}` — one cue on one position. */
  key: string;
  pLabel: string;
  fileId: number;
  /** The cue: the overlay class name (the renderer's segmentKey). */
  overlay: string;
  /** The cue's headline. */
  description: string;
  /** The cue's advice line. */
  subText: string;
  /** The cue's readout cards, in draw order ("Shaft 52°"). */
  values: string[];
}

/** One cue's still, as freeFeedback lists them from disk. */
export interface CueStill {
  position: string;
  cue: string;
  stillUrl: string;
}

const positionNumber = (label: string) => {
  const n = parseInt(label.replace(/^P/, ""), 10);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

export function cueRowsFromBundle(
  bundle: OverlayBundle | null | undefined,
  fileIdToPLabel: Map<number, string>
): CueRow[] {
  const rows: CueRow[] = [];
  const byKey = new Map<string, CueRow>();
  for (const frame of bundle?.frames ?? []) {
    for (const p of frame.overlays ?? []) {
      // The overlay carries the phase the RENDERER drew it for; trust that first. The fileId map comes
      // from getPPositionFrames, which re-runs the position finder and can land on a different frame than
      // the render did (dev 17822: P2 as 43717 where the render drew 43709). The map is the fallback for
      // bundles rendered before the stamp existed.
      const pLabel = p.position || fileIdToPLabel.get(frame.fileId);
      if (!pLabel) continue; // no phase known for this overlay
      // Only shapes with text belong in the list; the rest are the drawing and its scaffolding.
      const texts = (p.shapes ?? [])
        .filter((s) => s.shapeType === "Text" && s.shapeKeyValues?.text)
        .map((s) => s.shapeKeyValues!);
      if (texts.length === 0) continue;
      const headline = texts.find((t) => t.card !== "true") ?? texts[0];
      const values = texts.filter((t) => t !== headline && t.card === "true").map((t) => t.text as string);

      const key = `${pLabel}:${p.overlay}`;
      const existing = byKey.get(key);
      if (existing) {
        // The same cue drawn twice on one position: one row, every number kept once.
        for (const v of values) if (!existing.values.includes(v)) existing.values.push(v);
        continue;
      }
      const row: CueRow = {
        key,
        pLabel,
        fileId: frame.fileId,
        overlay: p.overlay,
        description: (headline.text as string) || p.overlay,
        subText: (headline.subText as string) || "",
        values,
      };
      byKey.set(key, row);
      rows.push(row);
    }
  }
  // Position order; within a position, the order the renderer drew the cues (a stable sort keeps it).
  return rows
    .map((r, i) => ({ r, i }))
    .sort((a, b) => positionNumber(a.r.pLabel) - positionNumber(b.r.pLabel) || a.i - b.i)
    .map(({ r }) => r);
}

/** The cue's own still, or null when this upload has none for it (rendered before cue stills existed). */
export function cueStillFor(stills: CueStill[] | null | undefined, pLabel: string, cue: string): string | null {
  return stills?.find((s) => s.position === pLabel && s.cue === cue)?.stillUrl ?? null;
}

/** The next (+1) or previous (-1) cue's key. From nothing, the first cue; at either end, stay put. */
export function stepCue(rows: ReadonlyArray<{ key: string }>, current: string | null, delta: 1 | -1): string | null {
  if (rows.length === 0) return null;
  const at = current == null ? -1 : rows.findIndex((r) => r.key === current);
  if (at < 0) return delta > 0 ? rows[0].key : rows[rows.length - 1].key;
  return rows[Math.min(rows.length - 1, Math.max(0, at + delta))].key;
}
